import React, { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '../ui/card'
import { TRADE_META, filterWorksites } from '../../lib/capex-utils'
import type { CapexPlan, Worksite, FilterTab, TradeCategory } from './types'
import { GridHeader } from './GridHeader'
import { TradeSection } from './TradeSection'
import { BudgetFooter } from './BudgetFooter'
import { EmptyState } from '../ui/empty-state'

interface PlanGridProps {
  plan: CapexPlan
  worksites: Worksite[]
  filter: FilterTab
  sortByUnlockedRent?: boolean
  collapsedTrades: Set<string>
  org: string
  onToggleTrade: (trade: string) => void
  isReadOnly: boolean
  onUpdateWorksite: (uid: string, body: Record<string, unknown>, ifMatch: string) => void
  onDeleteWorksite: (uid: string) => void
  onQuote?: (pkg: import('./types').WorkPackage) => void
  onDeleteQuote?: (pkg: import('./types').WorkPackage) => void
  onAddPackage?: (worksiteUid: string) => void
  onEditWorksite?: (worksite: import('./types').Worksite) => void
  onEditPackage?: (pkg: import('./types').WorkPackage) => void
}

export const PlanGrid = React.memo(function PlanGrid({
  plan,
  worksites,
  filter,
  sortByUnlockedRent,
  collapsedTrades,
  org,
  onToggleTrade,
  isReadOnly,
  onUpdateWorksite,
  onDeleteWorksite,
  onQuote,
  onDeleteQuote,
  onAddPackage,
  onEditWorksite,
  onEditPackage,
}: PlanGridProps) {
  const { t } = useTranslation('capex')

  const horizonStart = plan.horizon_start ?? new Date().getFullYear()
  const horizonEnd = plan.horizon_end ?? horizonStart + 10

  // Apply filter
  const filteredWorksites = useMemo(() => {
    const list = filterWorksites(worksites, filter)
    if (!sortByUnlockedRent) return list
    const risk = (ws: Worksite) => ws.unlocked_rent?.rent_at_risk ?? 0
    const unverified = (ws: Worksite) => ws.unlocked_rent?.rent_unverified ?? 0
    return [...list].sort((a, b) => risk(b) - risk(a) || unverified(b) - unverified(a))
  }, [worksites, filter, sortByUnlockedRent])

  // Group by trade, sort by TRADE_META order
  const tradeGroups = useMemo(() => {
    const groups: Map<string, Worksite[]> = new Map()

    for (const ws of filteredWorksites) {
      const trade = ws.trade ?? 'structural'
      if (!groups.has(trade)) {
        groups.set(trade, [])
      }
      groups.get(trade)!.push(ws)
    }

    // Sort trade groups by TRADE_META order
    const top = (list: Worksite[]) => Math.max(0, ...list.map((w) => w.unlocked_rent?.rent_at_risk ?? 0))
    const topUnverified = (list: Worksite[]) => Math.max(0, ...list.map((w) => w.unlocked_rent?.rent_unverified ?? 0))
    return Array.from(groups.entries()).sort(([a, la], [b, lb]) => {
      if (sortByUnlockedRent && top(la) !== top(lb)) return top(lb) - top(la)
      if (sortByUnlockedRent && topUnverified(la) !== topUnverified(lb)) return topUnverified(lb) - topUnverified(la)
      const orderA = TRADE_META[a as TradeCategory]?.order ?? 99
      const orderB = TRADE_META[b as TradeCategory]?.order ?? 99
      return orderA - orderB
    })
  }, [filteredWorksites, sortByUnlockedRent])

  const handleToggleTrade = useCallback(
    (trade: string) => {
      onToggleTrade(trade)
    },
    [onToggleTrade]
  )

  if (filteredWorksites.length === 0) {
    return (
      <Card data-testid="plan-grid-empty">
        <CardContent className="p-0">
          <EmptyState live className="h-40 justify-center bg-transparent" title={t('grid.no_worksites')} />
        </CardContent>
      </Card>
    )
  }

  return (
    <div
      className="overflow-x-auto overscroll-x-contain rounded-md border border-border"
      data-testid="plan-grid"
    >
      <table className="border-collapse">
        {/* Year column headers */}
        <GridHeader horizonStart={horizonStart} horizonEnd={horizonEnd} />

        <tbody>
          {tradeGroups.map(([trade, tradeWorksites]) => (
            <TradeSection
              key={trade}
              trade={trade}
              worksites={tradeWorksites}
              horizonStart={horizonStart}
              horizonEnd={horizonEnd}
              org={org}
              isCollapsed={collapsedTrades.has(trade)}
              onToggle={() => handleToggleTrade(trade)}
              isReadOnly={isReadOnly}
              onUpdateWorksite={onUpdateWorksite}
              onDeleteWorksite={onDeleteWorksite}
              onQuote={onQuote}
              onDeleteQuote={onDeleteQuote}
              onAddPackage={onAddPackage}
              onEditWorksite={onEditWorksite}
              onEditPackage={onEditPackage}
            />
          ))}
        </tbody>

        {/* Budget footer with histogram */}
        <BudgetFooter
          worksites={filteredWorksites}
          horizonStart={horizonStart}
          horizonEnd={horizonEnd}
        />
      </table>
    </div>
  )
})
